import { use, useEffect, useMemo, useState } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import { jsonParseWithLowerCaseKeys } from 'shared/utils/string-utils';

import BaseAlarmScenarioConfiguration from 'features/residents/models/configurations/base-alarm-scenario-configuration';
import TimeFrame from 'features/residents/models/configurations/time-frame';
import { ScenarioActiveOption } from 'features/residents/models/scenario-active-option';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

export type AlarmScenarioConfigurationProps = {
  index: number;
  situationalOptionEnabled?: boolean;
};

type AlarmScenarioConfiguration = {
  configuration: BaseAlarmScenarioConfiguration;
  timeFrameValuesChanged: (newTimeFrames: TimeFrame[] | null, newActiveAllDay: boolean) => void;
  onAlarmActiveChanged: (event: SelectChangeEvent<number>) => void;
  alarmTimeoutChanged: (event: SelectChangeEvent<number>) => void;
  alarmTimeout: number;
  determineSelectedActiveOption: () => ScenarioActiveOption;
};

export const useAlarmScenarioConfiguration = ({
  index,
  situationalOptionEnabled,
}: AlarmScenarioConfigurationProps): AlarmScenarioConfiguration => {
  const {
    detailEditState: { viewingMode, currentResidentSetting, residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);
  const rawConfiguration = useMemo(
    () => currentResidentSetting?.organisationUnitScenarios[index].configuration,
    [currentResidentSetting, index],
  );

  const [configuration, setConfiguration] = useState(jsonParseWithLowerCaseKeys(rawConfiguration) as BaseAlarmScenarioConfiguration);

  const scenarioConfigurationUpdated = (newConfiguration: unknown) => {
    residentSettingEditForm.setValue(`organisationUnitScenarios.${index}.configuration`, JSON.stringify(newConfiguration), {
      shouldDirty: true,
    });
  };

  const timeFrameValuesChanged = (newTimeFrames: TimeFrame[] | null, newActiveAllDay: boolean): void => {
    const newConfig = {
      ...configuration,
      timeFrames: newTimeFrames,
      sendAlarmAllDay: newActiveAllDay,
    };
    scenarioConfigurationUpdated(newConfig);
    setConfiguration(newConfig);
  };

  const alarmActiveChanged = (newIsAlarmActive: boolean, newIsStatusActive: boolean) => {
    const newConfig = {
      ...configuration,
      isAlarmActive: newIsAlarmActive,
      isStatusActive: newIsStatusActive,
    };
    scenarioConfigurationUpdated(newConfig);
    setConfiguration(newConfig);
  };

  const alarmTimeoutChanged = (event: SelectChangeEvent<number>) => {
    const newConfig = {
      ...configuration,
      alarmTimeOut: event.target.value,
    };
    scenarioConfigurationUpdated(newConfig);
    setConfiguration(newConfig);
  };

  const onAlarmActiveChanged = (event: SelectChangeEvent<number>) => {
    const selectedValue = event.target.value as ScenarioActiveOption;
    switch (selectedValue) {
      case ScenarioActiveOption.AlarmsOnly:
        alarmActiveChanged(true, false);
        return;
      case ScenarioActiveOption.StatusAndAlarms:
        alarmActiveChanged(true, true);
        return;
      case ScenarioActiveOption.StatusOnly:
        alarmActiveChanged(false, true);
        return;
      default:
        break;
    }
  };

  const possibleValues = useMemo(
    () => [...(situationalOptionEnabled ? [-1] : []), 0, 60, 300, 900, 1800, 3600],
    [situationalOptionEnabled],
  );
  const alarmTimeout = useMemo(
    () => (configuration.alarmTimeOut && possibleValues.find((x) => x === configuration.alarmTimeOut) ? configuration.alarmTimeOut : 0),
    [possibleValues, configuration],
  );

  useEffect(() => {
    residentSettingEditForm.clearErrors(`organisationUnitScenarios.${index}`);
    setConfiguration(jsonParseWithLowerCaseKeys(rawConfiguration) as BaseAlarmScenarioConfiguration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawConfiguration, viewingMode]);

  const determineSelectedActiveOption = (): ScenarioActiveOption => {
    if (configuration.isAlarmActive && !configuration.isStatusActive) {
      return ScenarioActiveOption.AlarmsOnly;
    } else if (configuration.isStatusActive && !configuration.isAlarmActive) {
      return ScenarioActiveOption.StatusOnly;
    }

    return ScenarioActiveOption.StatusAndAlarms;
  };

  return {
    configuration,
    alarmTimeout,
    timeFrameValuesChanged,
    onAlarmActiveChanged,
    alarmTimeoutChanged,
    determineSelectedActiveOption,
  };
};
